import axios from 'axios';

const API_BASE =
  process.env.REACT_APP_API_URL || 'https://smartedus.com/schoolerp/api';

const api = axios.create({
    baseURL: API_BASE,
});

// Request interceptor — attach token
// ============================================================
// AUTHENTICATION
// ============================================================

const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes

let inactivityTimer = null;

// Logout user
export const logoutUser = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');

  // Remove any other authentication information if present
  localStorage.removeItem('user');
  localStorage.removeItem('currentUser');

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Reset inactivity timer
const resetInactivityTimer = () => {
  if (!localStorage.getItem('access_token')) {
    return;
  }

  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }

  inactivityTimer = setTimeout(() => {
    console.log('Session expired due to 15 minutes of inactivity.');

    logoutUser();
  }, SESSION_TIMEOUT);
};

// User activity events
const activityEvents = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

// Register activity listeners
activityEvents.forEach((event) => {
  window.addEventListener(event, resetInactivityTimer);
});

// Start timer if already logged in
if (localStorage.getItem('access_token')) {
  resetInactivityTimer();
}


// ============================================================
// AXIOS REQUEST INTERCEPTOR
// ============================================================

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);


// ============================================================
// AXIOS RESPONSE INTERCEPTOR
// IMPORTANT:
// DO NOT REFRESH TOKEN AUTOMATICALLY
// ============================================================

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {

    if (error.response?.status === 401) {

      console.log('JWT expired or unauthorized. Logging out.');

      logoutUser();

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// ---- Auth ----
export const authAPI = {
  login: (data) => api.post('/auth/token/', data),
  refresh: (data) => api.post('/auth/token/refresh/', data),
  signup: (data) => api.post("/auth/signup/", data),
};


// ---- Students ----
export const studentsAPI = {
  list: (params) => api.get('/students/students/', { params }),
  get: (id) => api.get(`/students/students/${id}/`),
  create: (data) => api.post('/students/students/', data),
  update: (id, data) => api.put(`/students/students/${id}/`, data),
  delete: (id) => api.delete(`/students/students/${id}/`),
  stats: () => api.get('/students/students/stats/'),
  // Classes
  classes: (params) => api.get('/students/classes/', { params }),
  createClass: (data) => api.post('/students/classes/', data),
  updateClass: (id, data) => api.put(`/students/classes/${id}/`, data),
  deleteClass: (id) => api.delete(`/students/classes/${id}/`),
  // Sections
  sections: (params) => api.get('/students/sections/', { params }),
  createSection: (data) => api.post('/students/sections/', data),
  updateSection: (id, data) => api.put(`/students/sections/${id}/`, data),
  deleteSection: (id) => api.delete(`/students/sections/${id}/`),
  // Academic Years
  academicYears: () => api.get('/students/academic-years/'),
  createAcademicYear: (data) => api.post('/students/academic-years/', data),
  updateAcademicYear: (id, data) => api.put(`/students/academic-years/${id}/`, data),
  deleteAcademicYear: (id) => api.delete(`/students/academic-years/${id}/`),
  // Other
  discipline: (params) => api.get('/students/discipline/', { params }),
  createDiscipline: (data) => api.post('/students/discipline/', data),
  promotions: (params) => api.get('/students/promotions/', { params }),
  transfers: (params) => api.get('/students/transfers/', { params }),
};

// ---- Staff ----
export const staffAPI = {
  // Staff
  list: (params) => api.get('/staff/staff/', { params }),
  get: (id) => api.get(`/staff/staff/${id}/`),
  create: (data) => api.post('/staff/staff/', data),
  update: (id, data) => api.put(`/staff/staff/${id}/`, data),
  delete: (id) => api.delete(`/staff/staff/${id}/`),
  stats: () => api.get('/staff/staff/stats/'),

  // Departments
  departments: () => api.get('/staff/departments/'),
  createDepartment: (data) => api.post('/staff/departments/', data),
  updateDepartment: (id, data) => api.put(`/staff/departments/${id}/`, data),
  deleteDepartment: (id) => api.delete(`/staff/departments/${id}/`),

  // Leaves
  leaves: (params) => api.get('/staff/leaves/', { params }),
  createLeave: (data) => api.post('/staff/leaves/', data),
  updateLeave: (id, data) => api.patch(`/staff/leaves/${id}/`, data),

  // Payroll
  payroll: (params) => api.get('/staff/payroll/', { params }),
  payrollDetail: (id) => api.get(`/staff/payroll/${id}/`),
  createPayroll: (data) => api.post('/staff/payroll/', data),
  updatePayroll: (id, data) => api.put(`/staff/payroll/${id}/`, data),
  patchPayroll: (id, data) => api.patch(`/staff/payroll/${id}/`, data),
  deletePayroll: (id) => api.delete(`/staff/payroll/${id}/`),

  // Process Salary
  processSalary: (data) => api.post('/staff/payroll/process_salary/', data),

  // Payslip
  downloadPayslip: (id) =>
    api.get(`/staff/payroll/${id}/download_payslip/`, {
      responseType: "blob",
    }),

  // Performance
  performance: (params) => api.get('/staff/performance/', { params }),

  // Class Sections
  getClassSections: () => api.get('/class-sections/'),
};

// ---- Academic ----
export const academicAPI = {
  subjects: (params) => api.get('/academic/subjects/', { params }),
  createSubject: (data) => api.post('/academic/subjects/', data),
  updateSubject: (id, data) => api.put(`/academic/subjects/${id}/`, data),
  deleteSubject: (id) => api.delete(`/academic/subjects/${id}/`),
  timetable: (params) => api.get('/academic/timetable/', { params }),
  createTimetable: (data) => api.post('/academic/timetable/', data),
  syllabus: (params) => api.get('/academic/syllabus/', { params }),
  createSyllabus: (data) => api.post('/academic/syllabus/', data),
  assignments: (params) => api.get('/academic/assignments/', { params }),
  createAssignment: (data) => api.post('/academic/assignments/', data),
  submissions: (params) => api.get('/academic/submissions/', { params }),
};

// ---- Examination ----
export const examAPI = {
  exams: (params) => api.get('/examination/exams/', { params }),
  createExam: (data) => api.post('/examination/exams/', data),
  updateExam: (id, data) => api.put(`/examination/exams/${id}/`, data),
  schedules: (params) => api.get('/examination/schedules/', { params }),
  createSchedule: (data) => api.post('/examination/schedules/', data),
  results: (params) => api.get('/examination/results/', { params }),
  createResult: (data) => api.post('/examination/results/', data),
  updateResult: (id, data) => api.patch(`/examination/results/${id}/`, data),
  reportCard: (params) => api.get('/examination/results/report_card/', { params }),
};

// ---- Fees ----



// ---- Fees ----

export const feesAPI = {
  // ==================================================
  // FEE SETTINGS
  // ==================================================
  settings: () =>
    api.get("fees/fee-settings/"),

  setting: (id) =>
    api.get(`fees/fee-settings/${id}/`),

  createSetting: (data) =>
    api.post("fees/fee-settings/", data),

  updateSetting: (id, data) =>
    api.put(`fees/fee-settings/${id}/`, data),

  deleteSetting: (id) =>
    api.delete(`fees/fee-settings/${id}/`),


  // ==================================================
  // FEE REMINDERS
  // ==================================================
  reminders: () =>
    api.get("fees/fee-reminders/"),

  createReminder: (data) =>
    api.post("fees/fee-reminders/", data),

  updateReminder: (id, data) =>
    api.put(`fees/fee-reminders/${id}/`, data),

  deleteReminder: (id) =>
    api.delete(`fees/fee-reminders/${id}/`),


  // ==================================================
  // FEE TYPES / FEE CATEGORY
  // ==================================================
  feeTypes: () =>
    api.get("fees/fee-types/"),

  feeType: (id) =>
    api.get(`fees/fee-types/${id}/`),

  createFeeType: (data) =>
    api.post("fees/fee-types/", data),

  updateFeeType: (id, data) =>
    api.put(`fees/fee-types/${id}/`, data),

  deleteFeeType: (id) =>
    api.delete(`fees/fee-types/${id}/`),


  // ==================================================
  // FEE INSTALLMENTS
  // ==================================================
  installments: () =>
    api.get("fees/fee-installments/"),

  installment: (id) =>
    api.get(`fees/fee-installments/${id}/`),

  createInstallment: (data) =>
    api.post("fees/fee-installments/", data),

  updateInstallment: (id, data) =>
    api.put(`fees/fee-installments/${id}/`, data),

  deleteInstallment: (id) =>
    api.delete(`fees/fee-installments/${id}/`),


  // ==================================================
  // FEE STRUCTURES
  // ==================================================
  curriculums: () =>
    api.get("fees/curriculums/"),

  structures: () =>
    api.get("fees/fee-structures/"),

  structure: (id) =>
    api.get(`fees/fee-structures/${id}/`),

  createStructure: (data) =>
    api.post("fees/fee-structures/", data),

  updateStructure: (id, data) =>
    api.put(`fees/fee-structures/${id}/`, data),

  deleteStructure: (id) =>
    api.delete(`fees/fee-structures/${id}/`),


  // ==================================================
  // STUDENT FEE SEARCH
  // ==================================================
  searchStudentFees: (params = {}) =>
    api.get("fees/student-fees/search/", {
      params,
    }),


  // ==================================================
  // APPLY FEE
  // ==================================================
  applyFee: (data) =>
    api.post("fees/student-fees/apply_fee/", data),


  // ==================================================
  // FEE STRUCTURE TERMS
  // ==================================================
  structureTerms: () =>
    api.get("fees/fee-structure-terms/"),


  // ==================================================
  // FEE DISCOUNTS
  // ==================================================
  discounts: () =>
    api.get("fees/fee-discounts/"),

  discount: (id) =>
    api.get(`fees/fee-discounts/${id}/`),

  createDiscount: (data) =>
    api.post("fees/fee-discounts/", data),

  updateDiscount: (id, data) =>
    api.put(`fees/fee-discounts/${id}/`, data),

  deleteDiscount: (id) =>
    api.delete(`fees/fee-discounts/${id}/`),


  // ==================================================
  // STUDENT FEES
  // ==================================================
  studentFees: () =>
    api.get("fees/student-fees/"),

  studentFee: (id) =>
    api.get(`fees/student-fees/${id}/`),

  assignFee: (data) =>
    api.post("fees/student-fees/", data),

  updateAssignedFee: (id, data) =>
    api.put(`fees/student-fees/${id}/`, data),

  deleteAssignedFee: (id) =>
    api.delete(`fees/student-fees/${id}/`),

  studentFeesByStudent: (studentId) =>
    api.get(
      "fees/student-fees/by_student/",
      {
        params: {
          student_id: studentId,
        },
      }
    ),


  // ==================================================
  // COLLECTION SUMMARY
  // ==================================================
  collectionSummary: (params = {}) =>
    api.get(
      "fees/student-fees/collections/summary/",
      {
        params,
      }
    ),
	    collectPayment: (data) =>
        api.post(
            "fees/student-fees/collect-payment/",
            data
        ),
	   paymentHistory: (studentId) =>
        api.get(
            "fees/student-fees/payment-history/",
            {
                params: {
                    student_id: studentId,
                },
            }
        ),

  // Keep this alias because your CollectFee.js
  // may currently call feesAPI.summary()
  summary: (params = {}) =>
    api.get(
      "fees/student-fees/collections/summary/",
      {
        params,
      }
    ),


  // ==================================================
  // NOTE:
  // FEE COLLECTION ENDPOINTS ARE NOT AVAILABLE YET
  // ==================================================
  //
  // The following endpoints have NOT been registered
  // in your Django fees/urls.py:
  //
  // /fees/collections/
  // /fees/collections/{id}/
  // /fees/collections/pending/
  // /fees/collections/paid/
  // /fees/collections/by_student/
  //
  // Therefore do NOT call them until a
  // FeeCollectionViewSet is created.
  //
};

// ---- Attendance ----
export const attendanceAPI = {
  studentAttendance: (params) => api.get('/attendance/student/', { params }),
  markStudentAttendance: (data) => api.post('/attendance/student/', data),
  bulkMarkAttendance: (data) => api.post('/attendance/student/', data),
  staffAttendance: (params) => api.get('/attendance/staff/', { params }),
  markStaffAttendance: (data) => api.post('/attendance/staff/', data),
  leaveRequests: (params) => api.get('/attendance/leave-requests/', { params }),
  createLeaveRequest: (data) => api.post('/attendance/leave-requests/', data),
  updateLeaveRequest: (id, data) => api.patch(`/attendance/leave-requests/${id}/`, data),
  todaySummary: () => api.get('/attendance/student/today_summary/'),
};

// ---- Communication ----
export const communicationAPI = {
  announcements: (params) => api.get('/communication/announcements/', { params }),
  createAnnouncement: (data) => api.post('/communication/announcements/', data),
  messages: (params) => api.get('/communication/messages/', { params }),
  sendMessage: (data) => api.post('/communication/messages/', data),
  circulars: (params) => api.get('/communication/circulars/', { params }),
  createCircular: (data) => api.post('/communication/circulars/', data),
};

// ---- Transport ----
export const transportAPI = {
  vehicles: (params) => api.get('/transport/vehicles/', { params }),
  createVehicle: (data) => api.post('/transport/vehicles/', data),
  drivers: (params) => api.get('/transport/drivers/', { params }),
  createDriver: (data) => api.post('/transport/drivers/', data),
  routes: (params) => api.get('/transport/routes/', { params }),
  createRoute: (data) => api.post('/transport/routes/', data),
  studentTransport: (params) => api.get('/transport/student-transport/', { params }),
};

// ---- Library ----
export const libraryAPI = {
  books: (params) => api.get('/library/books/', { params }),
  createBook: (data) => api.post('/library/books/', data),
  updateBook: (id, data) => api.put(`/library/books/${id}/`, data),
  categories: () => api.get('/library/categories/'),
  issues: (params) => api.get('/library/issues/', { params }),
  issueBook: (data) => api.post('/library/issues/', data),
  returnBook: (id, data) => api.patch(`/library/issues/${id}/`, data),
  overdueBooks: () => api.get('/library/issues/overdue/'),
};

// ---- Hostel ----
export const hostelAPI = {
  hostels: () => api.get('/hostel/hostels/'),
  rooms: (params) => api.get('/hostel/rooms/', { params }),
  allocations: (params) => api.get('/hostel/allocations/', { params }),
  createAllocation: (data) => api.post('/hostel/allocations/', data),
  attendance: (params) => api.get('/hostel/attendance/', { params }),
  messMenu: (params) => api.get('/hostel/mess-menu/', { params }),
  visitors: (params) => api.get('/hostel/visitors/', { params }),
  createVisitor: (data) => api.post('/hostel/visitors/', data),
};

// ---- Reports ----
export const reportsAPI = {
  dashboard: () => api.get('/reports/dashboard/summary/'),
  templates: () => api.get('/reports/templates/'),
  generated: () => api.get('/reports/generated/'),
};

// ---- Administration ----
export const adminAPI = {
  users: (params) => api.get('/administration/users/', { params }),
  createUser: (data) => api.post('/administration/users/', data),
  updateUser: (id, data) => api.patch(`/administration/users/${id}/`, data),
  profiles: (params) => api.get('/administration/profiles/', { params }),
  auditLogs: (params) => api.get('/administration/audit-logs/', { params }),
  systemConfig: () => api.get('/administration/system-config/'),
  updateConfig: (id, data) => api.patch(`/administration/system-config/${id}/`, data),
  backups: () => api.get('/administration/backups/'),
  roles:()=>api.get("/administration/roles/"),
  permissions:()=>api.get("/administration/permissions/"),
  updatePermission: (id, data) => api.patch(`/administration/permissions/${id}/`, data),
  deleteUser: (id) =>api.delete(`/administration/users/${id}/`),
  currentUser: () => api.get("/administration/current-user/"),
};

export default api;

// ─── Ticket Management ─────────────────────────────────────────────────
export const ticketsAPI = {
  list: (params) => api.get('/tickets/tickets/', { params }),
  get: (id) => api.get(`/tickets/tickets/${id}/`),
  create: (data) => api.post('/tickets/tickets/', data),
  update: (id, data) => api.patch(`/tickets/tickets/${id}/`, data),
  delete: (id) => api.delete(`/tickets/tickets/${id}/`),
  stats: () => api.get('/tickets/tickets/stats/'),
  resolve: (id, data) => api.post(`/tickets/tickets/${id}/resolve/`, data),
  close: (id) => api.post(`/tickets/tickets/${id}/close/`),
  reopen: (id) => api.post(`/tickets/tickets/${id}/reopen/`),
  categories: () => api.get('/tickets/categories/'),
  createCategory: (data) => api.post('/tickets/categories/', data),
  comments: (ticketId) => api.get('/tickets/comments/', { params: { ticket: ticketId } }),
  addComment: (data) => api.post('/tickets/comments/', data),
  escalate: (data) => api.post('/tickets/escalations/', data),
};

// ─── Inventory Management ──────────────────────────────────────────────
export const inventoryAPI = {
  items: (params) => api.get('/inventory/items/', { params }),
  getItem: (id) => api.get(`/inventory/items/${id}/`),
  createItem: (data) => api.post('/inventory/items/', data),
  updateItem: (id, data) => api.patch(`/inventory/items/${id}/`, data),
  deleteItem: (id) => api.delete(`/inventory/items/${id}/`),
  stats: () => api.get('/inventory/items/stats/'),
  lowStockAlerts: () => api.get('/inventory/items/low_stock_alerts/'),
  categories: () => api.get('/inventory/categories/'),
  createCategory: (data) => api.post('/inventory/categories/', data),
  vendors: (params) => api.get('/inventory/vendors/', { params }),
  createVendor: (data) => api.post('/inventory/vendors/', data),
  updateVendor: (id, data) => api.patch(`/inventory/vendors/${id}/`, data),
  purchaseOrders: (params) => api.get('/inventory/purchase-orders/', { params }),
  createPurchaseOrder: (data) => api.post('/inventory/purchase-orders/', data),
  updatePurchaseOrder: (id, data) => api.patch(`/inventory/purchase-orders/${id}/`, data),
  transactions: (params) => api.get('/inventory/transactions/', { params }),
  createTransaction: (data) => api.post('/inventory/transactions/', data),
  allocations: (params) => api.get('/inventory/allocations/', { params }),
  createAllocation: (data) => api.post('/inventory/allocations/', data),
  updateAllocation: (id, data) => api.patch(`/inventory/allocations/${id}/`, data),
};

// ─── Inquiry Management ────────────────────────────────────────────────
export const inquiryAPI = {
  list: (params) => api.get('/inquiry/inquiries/', { params }),
  get: (id) => api.get(`/inquiry/inquiries/${id}/`),
  create: (data) => api.post('/inquiry/inquiries/', data),
  update: (id, data) => api.patch(`/inquiry/inquiries/${id}/`, data),
  delete: (id) => api.delete(`/inquiry/inquiries/${id}/`),
  stats: () => api.get('/inquiry/inquiries/stats/'),
  sourceAnalysis: () => api.get('/inquiry/inquiries/source_analysis/'),
  followUps: (params) => api.get('/inquiry/follow-ups/', { params }),
  createFollowUp: (data) => api.post('/inquiry/follow-ups/', data),
  commLogs: (params) => api.get('/inquiry/communication-logs/', { params }),
  createCommLog: (data) => api.post('/inquiry/communication-logs/', data),
};

// ─── HR Management ────────────────────────────────────────────────────
export const hrAPI = {
  jobs: (params) => api.get('/hr/jobs/', { params }),
  createJob: (data) => api.post('/hr/jobs/', data),
  updateJob: (id, data) => api.patch(`/hr/jobs/${id}/`, data),
  deleteJob: (id) => api.delete(`/hr/jobs/${id}/`),
  hrStats: () => api.get('/hr/jobs/stats/'),
  applications: (params) => api.get('/hr/applications/', { params }),
  updateApplication: (id, data) => api.patch(`/hr/applications/${id}/`, data),
  trainings: (params) => api.get('/hr/training-programs/', { params }),
  createTraining: (data) => api.post('/hr/training-programs/', data),
  updateTraining: (id, data) => api.patch(`/hr/training-programs/${id}/`, data),
  enrollments: (params) => api.get('/hr/training-enrollments/', { params }),
  createEnrollment: (data) => api.post('/hr/training-enrollments/', data),
  updateEnrollment: (id, data) => api.patch(`/hr/training-enrollments/${id}/`, data),
  appraisals: (params) => api.get('/hr/appraisals/', { params }),
  createAppraisal: (data) => api.post('/hr/appraisals/', data),
  updateAppraisal: (id, data) => api.patch(`/hr/appraisals/${id}/`, data),
  policies: (params) => api.get('/hr/policies/', { params }),
  createPolicy: (data) => api.post('/hr/policies/', data),
  updatePolicy: (id, data) => api.patch(`/hr/policies/${id}/`, data),
  staffDocs: (params) => api.get('/hr/staff-documents/', { params }),
  uploadStaffDoc: (data) => api.post('/hr/staff-documents/', data),
};

// ─── Grade Management ─────────────────────────────────────────────────
export const gradesAPI = {
  systems: () => api.get('/grades/grading-systems/'),
  createSystem: (data) => api.post('/grades/grading-systems/', data),
  scales: (params) => api.get('/grades/grade-scales/', { params }),
  createScale: (data) => api.post('/grades/grade-scales/', data),
  updateScale: (id, data) => api.patch(`/grades/grade-scales/${id}/`, data),
  deleteScale: (id) => api.delete(`/grades/grade-scales/${id}/`),
  exams: (params) => api.get('/grades/exams/', { params }),
  createExam: (data) => api.post('/grades/exams/', data),
  updateExam: (id, data) => api.patch(`/grades/exams/${id}/`, data),
  examStats: () => api.get('/grades/exams/stats/'),
  publishResults: (id) => api.post(`/grades/exams/${id}/publish_results/`),
  examSubjects: (params) => api.get('/grades/exam-subjects/', { params }),
  createExamSubject: (data) => api.post('/grades/exam-subjects/', data),
  updateExamSubject: (id, data) => api.patch(`/grades/exam-subjects/${id}/`, data),
  marks: (params) => api.get('/grades/marks/', { params }),
  createMarks: (data) => api.post('/grades/marks/', data),
  updateMarks: (id, data) => api.patch(`/grades/marks/${id}/`, data),
  classPerformance: (params) => api.get('/grades/marks/class_performance/', { params }),
  reportCards: (params) => api.get('/grades/report-cards/', { params }),
  publishReportCard: (id) => api.post(`/grades/report-cards/${id}/publish/`),
  toppers: (params) => api.get('/grades/report-cards/class_toppers/', { params }),
  subjectSummaries: (params) => api.get('/grades/subject-summaries/', { params }),
};

// ─── Branch Management ────────────────────────────────────────────────
export const branchesAPI = {
  list: (params) => api.get('/branches/branches/', { params }),
  get: (id) => api.get(`/branches/branches/${id}/`),
  create: (data) => api.post('/branches/branches/', data),
  update: (id, data) => api.patch(`/branches/branches/${id}/`, data),
  delete: (id) => api.delete(`/branches/branches/${id}/`),
  stats: () => api.get('/branches/branches/stats/'),
  performance: (id) => api.get(`/branches/branches/${id}/performance_summary/`),
  staffAssignments: (params) => api.get('/branches/staff-assignments/', { params }),
  createStaffAssignment: (data) => api.post('/branches/staff-assignments/', data),
  updateStaffAssignment: (id, data) => api.patch(`/branches/staff-assignments/${id}/`, data),
  transfers: (params) => api.get('/branches/transfers/', { params }),
  createTransfer: (data) => api.post('/branches/transfers/', data),
  approveTransfer: (id, data) => api.post(`/branches/transfers/${id}/approve/`, data),
  rejectTransfer: (id, data) => api.post(`/branches/transfers/${id}/reject/`, data),
  settings: (params) => api.get('/branches/settings/', { params }),
  updateSettings: (id, data) => api.patch(`/branches/settings/${id}/`, data),
  createSettings: (data) => api.post('/branches/settings/', data),
};
//holidays events




export const eventsAPI = {
  getAll: () => api.get("academic/events-holidays/"),

  get: (id) =>
    api.get(`academic/events-holidays/${id}/`),

  create: (data) =>
    api.post("academic/events-holidays/", data),

  update: (id, data) =>
    api.put(`academic/events-holidays/${id}/`, data),

  delete: (id) =>
    api.delete(`academic/events-holidays/${id}/`),
};
export const institutionAPI = {
  getAll: () => api.get("institutions/"),

  get: (id) =>
    api.get(`institutions/${id}/`),

  create: (formData) =>
    api.post("institutions/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),

  update: (id, formData) =>
    api.put(`institutions/${id}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),

  delete: (id) =>
    api.delete(`institutions/${id}/`),
};

